import { useState } from 'react';
import { Alert, Button, Checkbox, Modal, SearchBar } from '@shared/components';
import { useDebounce } from '@shared/hooks/useDebounce';
import { isApiError } from '@/services/api';
import { useAttachDealersMutation } from '../hooks/useAttachDealersMutation';
import { useScopedDealersQuery } from '../hooks/useScopedDealersQuery';

const MAX_ATTACH = 200;

interface Props {
  groupId: string;
  ownerDealerId: string;
  attachedDealerIds: string[];
  onClose: () => void;
}

export function AttachDealersModal({ groupId, ownerDealerId, attachedDealerIds, onClose }: Props) {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [includeDescendants, setIncludeDescendants] = useState(false);
  const [outOfScopeIds, setOutOfScopeIds] = useState<string[]>([]);

  const debouncedSearch = useDebounce(search, 300);
  const dealersQuery = useScopedDealersQuery(ownerDealerId, debouncedSearch);
  const dealers = dealersQuery.data?.data ?? [];

  const attachMutation = useAttachDealersMutation(groupId);

  const toggle = (id: string) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const overCap = selected.length > MAX_ATTACH;

  const handleConfirm = () => {
    if (overCap || selected.length === 0) return;
    setOutOfScopeIds([]);
    attachMutation.mutate(
      { dealer_ids: selected, include_descendants: includeDescendants },
      {
        onSuccess: () => {
          setSelected([]);
          setIncludeDescendants(false);
          onClose();
        },
        onError: (err) => {
          if (!isApiError(err) || err.status !== 422) return;
          const body = err.data as { out_of_scope_ids?: unknown } | undefined;
          if (Array.isArray(body?.out_of_scope_ids)) {
            setOutOfScopeIds(body.out_of_scope_ids as string[]);
          }
        },
      },
    );
  };

  return (
    <Modal isOpen onClose={onClose} className="max-w-lg mx-4" ariaLabel="Attach Dealers">
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-semibold">Attach Dealers</h2>

        <SearchBar
          searchTerm={search}
          setSearchTerm={setSearch}
          placeholder="Search dealers"
        />

        <div className="space-y-2 max-h-64 overflow-y-auto">
          {dealersQuery.isLoading && <p className="text-sm text-mp-muted">Loading...</p>}
          {dealersQuery.isError && (
            <p className="text-mp-error text-sm">Unable to load dealers. You may not have permission to browse dealers in this hierarchy.</p>
          )}
          {!dealersQuery.isLoading && !dealersQuery.isError && dealers.length === 0 && (
            <p className="text-sm text-mp-muted">No dealers found.</p>
          )}
          {dealers.map((dealer) => {
            const alreadyAttached = attachedDealerIds.includes(dealer.id);
            const isChecked = selected.includes(dealer.id) || alreadyAttached;
            return (
              <div key={dealer.id} className="flex items-center gap-2">
                <Checkbox
                  id={`dealer-${dealer.id}`}
                  checked={isChecked}
                  onChange={() => !alreadyAttached && toggle(dealer.id)}
                  disabled={alreadyAttached || attachMutation.isPending}
                  ariaLabel={dealer.name}
                />
                <span className="text-sm">
                  {dealer.name}
                  {alreadyAttached && <span className="text-mp-muted ml-1">(already attached)</span>}
                </span>
              </div>
            );
          })}
        </div>

        <div className="space-y-1 border-t border-mp-border pt-3">
          <Checkbox
            id="include-descendants"
            checked={includeDescendants}
            onChange={setIncludeDescendants}
            disabled={attachMutation.isPending}
            label="Attach all linked Dealers to this Security Group *"
            labelClassName="text-sm"
            ariaLabel="Attach all linked Dealers to this Security Group"
          />
          <p className="text-mp-warning text-xs">
            * Please note that there is no easy way to un-attach Dealers on mass - so be careful when using this option
          </p>
        </div>

        <p className="text-xs text-mp-muted">
          Selected: {selected.length} / {MAX_ATTACH}
        </p>

        {overCap && (
          <p className="text-mp-error text-xs">
            You can attach a maximum of {MAX_ATTACH} dealers at a time.
          </p>
        )}

        {outOfScopeIds.length > 0 && (
          <Alert variant="warning" role="note" className="text-xs">
            <div>
              <p className="font-semibold">
                {outOfScopeIds.length} dealer{outOfScopeIds.length > 1 ? 's are' : ' is'} outside this security group&apos;s dealer scope and could not be attached.
              </p>
              <p className="mt-1 text-mp-muted">IDs: {outOfScopeIds.join(', ')}</p>
            </div>
          </Alert>
        )}

        {attachMutation.isError && outOfScopeIds.length === 0 && (
          <p className="text-mp-error text-xs">
            {isApiError(attachMutation.error)
              ? attachMutation.error.message
              : 'Failed to attach dealers. Please try again.'}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={attachMutation.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            size="sm"
            onClick={handleConfirm}
            disabled={selected.length === 0 || overCap || attachMutation.isPending}
          >
            {attachMutation.isPending ? 'Attaching...' : 'Attach'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
